import * as React from 'react';
import axios from 'axios';
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Skeleton,
    Tooltip,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { format, parseISO } from 'date-fns';
import { getAdminApiBaseUrl } from '../utils/adminApiBaseUrl';
import { ActiveDormantBucketSlug } from './CustomerInsightCharts';
import { fetchDashboardExport } from '../utils/dashboardExportApi';
import { downloadExcel } from '../utils/exportExcel';
import { useNotify } from 'react-admin';

type SortField =
    | 'company_name'
    | 'customer_username'
    | 'customer_name'
    | 'phone_mobile'
    | 'last_activity_at'
    | 'days_since_activity'
    | 'last_login'
    | 'registered_at';

type SortOrder = 'asc' | 'desc';

interface ActiveDormantCustomerRow {
    customer_id: number;
    company_name: string | null;
    customer_username: string | null;
    customer_name: string | null;
    phone_mobile: string | null;
    last_activity_at: string | null;
    days_since_activity: number | null;
    last_login: string | null;
    registered_at: string | null;
}

interface ActiveDormantCustomerModalProps {
    open: boolean;
    bucket: ActiveDormantBucketSlug | null;
    bucketLabel: string;
    expectedCount?: number;
    onClose: () => void;
}

const DEFAULT_SORT_BY_BUCKET: Record<ActiveDormantBucketSlug, { sort: SortField; order: SortOrder }> = {
    active_7d: { sort: 'last_activity_at', order: 'desc' },
    active_8_30d: { sort: 'last_activity_at', order: 'desc' },
    inactive_31_90d: { sort: 'last_activity_at', order: 'asc' },
    inactive_90d_plus: { sort: 'last_activity_at', order: 'asc' },
};

const COLUMNS: Array<{ id: SortField; label: string }> = [
    { id: 'company_name', label: 'Company' },
    { id: 'customer_username', label: 'Username' },
    { id: 'customer_name', label: 'Name' },
    { id: 'phone_mobile', label: 'Mobile' },
    { id: 'last_activity_at', label: 'Last Activity' },
    { id: 'days_since_activity', label: 'Days Since Activity' },
    { id: 'last_login', label: 'Last Login' },
    { id: 'registered_at', label: 'Registered' },
];

const formatDateTime = (value: string | null) => {
    if (!value) {
        return '—';
    }

    try {
        const parsed = parseISO(value);
        if (Number.isNaN(parsed.getTime())) {
            return value;
        }
        return format(parsed, 'MMM d, yyyy · h:mm a');
    } catch {
        return value;
    }
};

const formatDate = (value: string | null) => {
    if (!value) {
        return '—';
    }

    try {
        const parsed = parseISO(value);
        if (Number.isNaN(parsed.getTime())) {
            return value;
        }
        return format(parsed, 'MMM d, yyyy');
    } catch {
        return value;
    }
};

const ActiveDormantCustomerModal: React.FC<ActiveDormantCustomerModalProps> = ({
    open,
    bucket,
    bucketLabel,
    expectedCount,
    onClose,
}) => {
    const [rows, setRows] = React.useState<ActiveDormantCustomerRow[]>([]);
    const [total, setTotal] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(50);
    const [sort, setSort] = React.useState<SortField>('last_activity_at');
    const [order, setOrder] = React.useState<SortOrder>('desc');
    const [dataThrough, setDataThrough] = React.useState<string | null>(null);
    const [downloading, setDownloading] = React.useState(false);
    const notify = useNotify();

    const handleDownload = async () => {
        if (!bucket) return;
        setDownloading(true);
        try {
            const result = await fetchDashboardExport({
                panel: 'active_dormant',
                bucket,
            });
            if (!result.rows?.length) {
                notify('No rows to export for this bucket', { type: 'info' });
                return;
            }
            downloadExcel({
                filename: `active-dormant-${bucket}`,
                sheets: [{ name: 'Customers', rows: result.rows }],
            });
            notify(`Exported ${result.rows.length.toLocaleString()} row(s)`, { type: 'success' });
        } catch (error: any) {
            console.error(error);
            notify(error?.response?.data?.message || 'Export failed', { type: 'error' });
        } finally {
            setDownloading(false);
        }
    };

    React.useEffect(() => {
        if (!open || !bucket) {
            return;
        }

        const defaults = DEFAULT_SORT_BY_BUCKET[bucket];
        setPage(0);
        setSort(defaults.sort);
        setOrder(defaults.order);
        setRows([]);
        setTotal(0);
        setError(null);
    }, [open, bucket]);

    React.useEffect(() => {
        if (!open || !bucket) {
            return;
        }

        const fetchDetails = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await axios.get(`${getAdminApiBaseUrl()}/customers/active-dormant-details`, {
                    params: {
                        bucket,
                        limit: rowsPerPage,
                        offset: page * rowsPerPage,
                        sort,
                        order,
                    },
                });

                setRows(Array.isArray(response.data?.data) ? response.data.data : []);
                setTotal(Number(response.data?.total) || 0);
                setDataThrough(response.data?.data_through || null);
            } catch (fetchError: any) {
                console.error('Failed to load active/dormant customer details:', fetchError);
                setRows([]);
                setTotal(0);
                setError(fetchError?.response?.data?.message || 'Failed to load customer details.');
            } finally {
                setLoading(false);
            }
        };

        fetchDetails();
    }, [open, bucket, page, rowsPerPage, sort, order]);

    const handleSort = (field: SortField) => {
        const isSameField = sort === field;
        setSort(field);
        setOrder(isSameField ? (order === 'asc' ? 'desc' : 'asc') : field === 'last_activity_at' && bucket
            ? DEFAULT_SORT_BY_BUCKET[bucket].order
            : 'asc');
        setPage(0);
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const titleCount = total || expectedCount || 0;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
            <DialogTitle sx={{ pr: 12 }}>
                {bucketLabel} — {titleCount.toLocaleString()} customers
                <Box sx={{ position: 'absolute', right: 48, top: 12 }}>
                    <Tooltip title="Download Excel">
                        <span>
                            <IconButton
                                aria-label="Download Excel"
                                onClick={handleDownload}
                                disabled={downloading || !bucket}
                            >
                                {downloading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 12, top: 12 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {dataThrough && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        Data through {formatDate(dataThrough)} (nightly rollup)
                    </Typography>
                )}

                {error ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                ) : loading ? (
                    <Skeleton variant="rectangular" height={360} />
                ) : rows.length === 0 ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">No customers found in this bucket.</Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {COLUMNS.map(column => (
                                            <TableCell key={column.id} sortDirection={sort === column.id ? order : false}>
                                                <TableSortLabel
                                                    active={sort === column.id}
                                                    direction={sort === column.id ? order : 'asc'}
                                                    onClick={() => handleSort(column.id)}
                                                >
                                                    {column.label}
                                                </TableSortLabel>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map(row => (
                                        <TableRow key={row.customer_id} hover>
                                            <TableCell>{row.company_name || '—'}</TableCell>
                                            <TableCell>{row.customer_username || '—'}</TableCell>
                                            <TableCell>{row.customer_name || '—'}</TableCell>
                                            <TableCell>{row.phone_mobile || '—'}</TableCell>
                                            <TableCell>{formatDateTime(row.last_activity_at)}</TableCell>
                                            <TableCell>
                                                {row.days_since_activity == null ? '—' : row.days_since_activity.toLocaleString()}
                                            </TableCell>
                                            <TableCell>{formatDateTime(row.last_login)}</TableCell>
                                            <TableCell>{formatDate(row.registered_at)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[25, 50, 100]}
                        />
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ActiveDormantCustomerModal;
